"use client";

import { useState } from "react";
import { Briefcase, BookOpen, Heart, DollarSign, Home, Palette } from "lucide-react";

// Icon mapping for the theme
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  briefcase: Briefcase,
  book: BookOpen,
  heart: Heart,
  dollar: DollarSign,
  home: Home,
  palette: Palette,
};

type Option = {
  value: string;
  icon?: string;
};

type Props = {
  options: Option[];
  value: string[];
  onChange: (value: string[]) => void;
  maxSelections?: number;
  name: string;
};

export default function IconCardThemeCheckbox({ 
  options, 
  value = [], 
  onChange, 
  maxSelections = 3
}: Props) {
  const [animateLimit, setAnimateLimit] = useState(false);

  const handleToggle = (optionValue: string) => {
    const currentValues = value || [];
    const isSelected = currentValues.includes(optionValue);
    
    if (isSelected) {
      // Remove the value
      onChange(currentValues.filter(v => v !== optionValue));
    } else {
      // Add the value if under limit
      if (!maxSelections || currentValues.length < maxSelections) {
        onChange([...currentValues, optionValue]);
      } else {
        // Animate limit message
        setAnimateLimit(true);
        setTimeout(() => setAnimateLimit(false), 500);
      }
    }
  };

  return (
    <div className="space-y-6 px-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
        {options.map((option) => {
          const Icon = option.icon ? iconMap[option.icon] : null;
          const isSelected = value?.includes(option.value) || false;
          const isDisabled = !isSelected && maxSelections && value?.length >= maxSelections ? true : false;
          
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleToggle(option.value)}
              disabled={isDisabled}
              className={`p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                isSelected
                  ? "border-[#2A6354] bg-[#2A6354]/5 shadow-lg scale-105"
                  : isDisabled
                  ? "border-[#BFBCC0]/20 bg-gray-50 opacity-50 cursor-not-allowed"
                  : "border-[#BFBCC0]/30 bg-white hover:border-[#BFBCC0]/50 hover:shadow-md cursor-pointer"
              }`}
              role="checkbox"
              aria-checked={isSelected}
            >
              <div className="flex items-start gap-4">
                {Icon && (
                  <div
                    className={`p-3 rounded-lg transition-colors ${
                      isSelected
                        ? "bg-[#2A6354]/10"
                        : "bg-[#D2CED4]/20"
                    }`}
                  >
                    <Icon
                      className={`w-6 h-6 ${
                        isSelected
                          ? "text-[#2A6354]"
                          : "text-[#000000]/60"
                      }`}
                    />
                  </div>
                )}
                <div className="flex-1">
                  <p
                    className={`font-semibold ${
                      isSelected
                        ? "text-[#2A6354]"
                        : "text-[#000000]"
                    }`}
                    style={{ fontFamily: 'Arsenal, sans-serif' }}
                  >
                    {option.value}
                  </p>
                </div>
                {/* Checkmark indicator */}
                {isSelected && (
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#2A6354] text-white">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selection counter */}
      {maxSelections && (
        <div className="text-center">
          <p 
            className={`text-sm transition-all duration-300 ${
              animateLimit ? "text-[#EE7546] scale-110" : "text-gray-600"
            }`}
            style={{ fontFamily: 'Barlow, sans-serif' }}
          >
            {value?.length || 0} of {maxSelections} areas selected
            {value?.length >= maxSelections && " (maximum reached)"}
          </p>
        </div>
      )}
    </div>
  );
}